import { useEffect, useCallback } from 'react'

export interface ShortcutConfig {
  key: string
  modifiers: {
    ctrl?: boolean
    alt?: boolean
    shift?: boolean
  }
  description: string
  action: () => void
}

interface UseGlobalHotkeysOptions {
  shortcuts: ShortcutConfig[]
  enabled: boolean
}

// Check if an element should ignore keyboard shortcuts
function shouldIgnoreTarget(target: EventTarget | null): boolean {
  if (!target || !(target instanceof Element)) return false
  
  const tagName = target.tagName.toLowerCase()
  const isInput = tagName === 'input' || tagName === 'textarea'
  const isContentEditable = target.getAttribute('contenteditable') === 'true'
  const isCombobox = target.getAttribute('role') === 'combobox'
  
  // Check if we're inside a modal or dialog
  const isInsideModal = target.closest('[role="dialog"], [role="modal"], .modal')
  
  return isInput || isContentEditable || isCombobox || !!isInsideModal
}

// Normalize key to handle uppercase/lowercase
function normalizeKey(key: string): string {
  return key.toLowerCase()
}

// Check if modifiers match
function modifiersMatch(event: KeyboardEvent, modifiers: ShortcutConfig['modifiers']): boolean {
  return (
    !!event.ctrlKey === !!modifiers.ctrl &&
    !!event.altKey === !!modifiers.alt &&
    !!event.shiftKey === !!modifiers.shift
  )
}

export function useGlobalHotkeys({ shortcuts, enabled }: UseGlobalHotkeysOptions) {
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!enabled) return
    
    // Ignore if target should not trigger shortcuts
    if (shouldIgnoreTarget(event.target)) return
    
    const normalizedKey = normalizeKey(event.key)
    
    // Find matching shortcut
    const matchingShortcut = shortcuts.find(shortcut => 
      normalizeKey(shortcut.key) === normalizedKey &&
      modifiersMatch(event, shortcut.modifiers)
    )
    
    if (matchingShortcut) {
      event.preventDefault()
      event.stopPropagation()
      matchingShortcut.action()
    }
  }, [shortcuts, enabled])
  
  useEffect(() => {
    if (!enabled) return
    
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown, enabled])
}